import { Helmet } from "react-helmet-async";

const SITE_NAME = "audrivio";
const SITE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const DEFAULT_IMAGE = `${SITE_URL}/favicon-audrivio.png`;

const LANG_TO_LOCALE = {
  it: "it_IT",
  en: "en_GB",
  fr: "fr_FR",
  de: "de_DE",
  es: "es_ES",
};

// Default meta title/description per language when the page does not provide one.
const DEFAULTS = {
  it: {
    title: "audrivio | Autoradio Android Plug&Play",
    desc: "Autoradio Android Plug&Play dedicate. Installazione 30 minuti, garanzia 24 mesi, spedizione 24-48h in tutta Italia.",
  },
  en: {
    title: "audrivio | Plug&Play Android head units",
    desc: "Plug&Play Android head units tailored to your car model. 30-minute install, 24-month warranty, 24-48h shipping across the EU.",
  },
  fr: {
    title: "audrivio | Autoradios Android Plug&Play",
    desc: "Autoradios Android Plug&Play sur mesure pour votre modèle. Installation 30 min, garantie 24 mois, livraison 24-48h.",
  },
  de: {
    title: "audrivio | Plug&Play Android-Autoradios",
    desc: "Plug&Play Android-Autoradios speziell für Ihr Modell. 30-Minuten-Einbau, 24 Monate Garantie, Versand 24-48h.",
  },
  es: {
    title: "audrivio | Autorradios Android Plug&Play",
    desc: "Autorradios Android Plug&Play a medida para tu modelo. Instalación en 30 min, garantía 24 meses, envío 24-48h.",
  },
};

/**
 * Append `?lang=xx` to a path when the language differs from Italian (default market).
 * Keeps the site on a single URL scheme while giving Google distinct alternate URLs
 * for hreflang. The LanguageContext honours `?lang=xx` on first visit.
 */
function withLangParam(path, lang) {
  if (!lang || lang === "it") return path;
  const [base, hash] = path.split("#");
  const [pathname, query] = base.split("?");
  const qs = new URLSearchParams(query || "");
  qs.set("lang", lang);
  return `${pathname}?${qs.toString()}${hash ? `#${hash}` : ""}`;
}

/**
 * Central SEO component.
 *
 * Props:
 *   title       page-specific title (will be composed with " | audrivio")
 *   description meta description
 *   image       absolute URL to OG image (defaults to logo)
 *   path        path used for canonical + og:url (e.g. "/prodotto/xyz")
 *   lang        current app language (it/en/fr/de/es)
 *   type        og:type (website | product | article)
 *   noindex     add <meta name="robots" content="noindex">
 *   jsonLd      one or more JSON-LD objects (array or single)
 *   hreflangs   map of {lang: absolutePath} for alternate versions
 *   product     shortcut to auto-generate a Product JSON-LD
 */
export default function SEO({
  title,
  description,
  image,
  path = "/",
  lang = "it",
  type = "website",
  noindex = false,
  jsonLd,
  hreflangs,
  product,
}) {
  const defaults = DEFAULTS[lang] || DEFAULTS.it;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : defaults.title;
  const desc = description || defaults.desc;
  const canonical = `${SITE_URL}${withLangParam(path, lang)}`;
  const ogImage = image || DEFAULT_IMAGE;
  const locale = LANG_TO_LOCALE[lang] || "it_IT";

  const structuredData = [].concat(jsonLd || []).filter(Boolean);
  if (product) structuredData.push(buildProductLd(product, path));

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Hreflang alternates — each language gets its own URL via ?lang=xx query so
          Google indexes them as distinct pages, while the SPA still routes on one URL. */}
      {hreflangs &&
        Object.entries(hreflangs).map(([l, p]) => (
          <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}${withLangParam(p, l)}`} />
        ))}
      {hreflangs && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${hreflangs.it || path}`} />}

      {/* JSON-LD structured data */}
      {structuredData.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

// ---------- helpers exported for pages ----------

export function buildProductLd(product, path) {
  if (!product) return null;
  const url = `${SITE_URL}${path}`;
  const image = product.images?.[0] || DEFAULT_IMAGE;
  const price = product.price || product.variants?.[0]?.price || 0;
  const ld = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    image: (product.images || []).slice(0, 5),
    description: (product.description || "").slice(0, 500) || product.name,
    sku: product.variants?.[0]?.sku || product.id,
    brand: { "@type": "Brand", name: product.brand || SITE_NAME },
    ...(product.model ? { model: product.model } : {}),
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "EUR",
      price: Number(price).toFixed(2),
      availability: (product.stock ?? 1) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };
  if (product.rating && product.reviews_count) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(product.rating).toFixed(1),
      reviewCount: product.reviews_count,
    };
  }
  return ld;
}

export function buildBreadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function buildOrganizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    sameAs: [],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+39 351 129 4525",
        contactType: "customer support",
        areaServed: "IT",
        availableLanguage: ["Italian", "English"],
      },
    ],
  };
}

export function buildWebsiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/catalogo?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}
